import { type ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

/**
 * Left slide-in navigation drawer on a native modal <dialog>: the page behind
 * is inert, focus stays inside, Escape and a backdrop click close it, focus
 * returns to the opener, and the page behind doesn't scroll.
 * Built from tokens only. Temporary: to be replaced by an anthm-design Drawer
 * (see the design-system gaps in docs/design/web/WEB_PARITY_PLAN.md).
 */
export function MobileDrawer({
  open,
  onClose,
  children,
}: Readonly<{ open: boolean; onClose: () => void; children: ReactNode }>) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { t } = useTranslation("shell");

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!open || !dialog) return;
    const opener = document.activeElement as HTMLElement | null;
    // showModal makes the rest of the page inert; jsdom (tests) lacks it.
    if (!dialog.open) {
      if (typeof dialog.showModal === "function") dialog.showModal();
      else dialog.setAttribute("open", "");
    }
    dialog.querySelector<HTMLElement>("button, a, [tabindex]")?.focus();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Escape (also handled natively via onCancel; this covers environments
    // without showModal) and backdrop clicks (a click on the dialog element
    // itself, not its content). Attached here rather than as JSX props.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    const onBackdropClick = (e: MouseEvent) => {
      if (e.target === dialog) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    dialog.addEventListener("click", onBackdropClick);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      dialog.removeEventListener("click", onBackdropClick);
      document.body.style.overflow = overflow;
      opener?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <dialog
      ref={dialogRef}
      aria-label={t("nav.label")}
      className="fixed inset-y-0 left-0 m-0 flex h-dvh max-h-none max-w-[85vw] bg-transparent p-0 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] shadow-xl backdrop:bg-surface-overlay [&>nav]:h-full"
      // Native Escape: close via React state instead of the browser closing it.
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      {children}
    </dialog>
  );
}
